// Lancamento de Silhueta Metalica de VARIOS atiradores ao mesmo tempo (01/11/2026).
// Abre a partir do Mapa de Resultados: clica nos atiradores para marcar e clica de novo num marcado.
// Grava atirador por atirador pelas rotas de sempre (POST /resultados ou PUT /resultados/:id),
// entao a fila offline continua valendo. Se um falhar, os outros ficam gravados e a tela mostra so os que faltam.
import { useEffect, useRef, useState } from 'react'
import { useCreateResultado, useUpdateResultado } from '../hooks/useResultados'
import { bluetoothPrinter } from '../../../lib/bluetoothPrinter'
import type { MapaResultadoItem } from '../types'

const FIGURAS = ['galinha', 'porco', 'peru', 'carneiro'] as const
type Figura = typeof FIGURAS[number]
type Valores = Record<Figura, string>

const ZERO: Valores = { galinha: '0', porco: '0', peru: '0', carneiro: '0' }
const DRAFT_KEY = 'resultado_lote_silhueta_draft'

function num(v: string): number {
  const n = parseInt(v || '0', 10)
  return Number.isFinite(n) && n > 0 ? n : 0
}

function total(v: Valores): number {
  return FIGURAS.reduce((s, f) => s + num(v[f]), 0)
}

function esc(s: unknown): string {
  return String(s ?? '').replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c] as string))
}

interface CupomSilhueta {
  atirador_nome: string
  modalidade_nome: string
  valores: Valores
}

// Bluetooth: um cupom atras do outro. Sem Bluetooth: um unico dialogo de impressao com um cupom por pagina.
async function imprimirCupons(cupons: (CupomSilhueta & { armaDistancia: string })[], avisar: (m: string, t: 'success' | 'error') => void) {
  if (cupons.length === 0) return
  if (bluetoothPrinter.isConnected()) {
    try {
      for (const c of cupons) {
        await bluetoothPrinter.printResultado({
          atirador_nome: c.atirador_nome,
          modalidade_nome: c.modalidade_nome,
          grupo: 'Silhueta',
          armaDistancia: c.armaDistancia,
          galinha: num(c.valores.galinha),
          porco: num(c.valores.porco),
          peru: num(c.valores.peru),
          carneiro: num(c.valores.carneiro),
          totalAcertos: total(c.valores),
        })
      }
      avisar(`${cupons.length} cupom(ns) enviado(s) para a impressora Bluetooth`, 'success')
      return
    } catch (e: any) {
      avisar(`Bluetooth: ${e?.message ?? 'Erro'} — usando impressao padrao`, 'error')
    }
  }

  const agora = new Date().toLocaleString('pt-BR')
  const paginas = cupons.map(c => `
    <section class="cupom">
      <div class="center" style="font-size:11px; margin-bottom:2px; text-transform:uppercase;">CAMPEONATO DE TIRO</div>
      <div class="sep-solid"></div>
      <div class="nome">${esc(c.atirador_nome)}</div>
      <div class="modalidade">${esc(c.modalidade_nome)}</div>
      ${c.armaDistancia ? `<div class="arma">${esc(c.armaDistancia)}</div>` : ''}
      <div class="sep"></div>
      <p class="linha">GALINHA = ${num(c.valores.galinha)}</p>
      <p class="linha">PORCO = ${num(c.valores.porco)}</p>
      <p class="linha">PERU = ${num(c.valores.peru)}</p>
      <p class="linha" style="margin-bottom:4px;">CARNEIRO = ${num(c.valores.carneiro)}</p>
      <div class="sep"></div>
      <div class="totais"><p class="total-pts">PONTOS = ${total(c.valores)}</p></div>
      <div class="sep"></div>
      <div class="rodape">${agora}</div>
      <div class="assin"><p>Assinatura: _______________________________</p></div>
    </section>`).join('')

  const conteudo = `<!DOCTYPE html><html lang="pt-BR"><head><meta charset="UTF-8" /><title>Comprovantes de Resultado</title>
    <style>
      * { margin: 0; padding: 0; box-sizing: border-box; }
      body { font-family: 'Courier New', Courier, monospace; font-size: 12px; color: #000; background: #fff; width: 80mm; max-width: 80mm; line-height: 1.3; }
      @media print { @page { size: 80mm auto; margin: 0; } body { width: 80mm; max-width: 80mm; } }
      .cupom { padding: 4px; page-break-after: always; break-after: page; }
      .cupom:last-child { page-break-after: auto; break-after: auto; }
      .center { text-align: center; }
      .sep { border-top: 1px dashed #000; margin: 4px 0; }
      .sep-solid { border-top: 1px solid #000; margin: 4px 0; }
      .nome { font-size: 14px; font-weight: bold; text-align: center; text-transform: uppercase; margin-bottom: 2px; word-break: break-word; }
      .modalidade { font-size: 12px; font-weight: bold; text-align: center; text-transform: uppercase; margin-bottom: 1px; }
      .arma { font-size: 12px; text-align: center; text-transform: uppercase; margin-bottom: 3px; }
      .linha { font-size: 12px; font-weight: bold; text-transform: uppercase; margin: 3px 0 2px; }
      .totais { margin-top: 4px; font-size: 12px; font-weight: bold; }
      .total-pts { font-size: 13px; font-weight: bold; text-transform: uppercase; }
      .rodape { font-size: 11px; text-align: center; margin-top: 6px; }
      .assin { margin-top: 8px; border-top: 1px solid #000; padding-top: 4px; font-size: 11px; }
    </style></head><body>${paginas}</body></html>`

  document.getElementById('__thermal_print_frame')?.remove()
  const frame = document.createElement('iframe')
  frame.id = '__thermal_print_frame'
  frame.style.cssText = 'position:fixed;top:0;left:0;width:0;height:0;border:none;visibility:hidden;'
  document.body.appendChild(frame)
  const doc = frame.contentDocument || frame.contentWindow?.document
  if (doc) { doc.open(); doc.write(conteudo); doc.close() }
  setTimeout(() => {
    try { frame.contentWindow?.focus(); frame.contentWindow?.print() } finally { setTimeout(() => frame.remove(), 2000) }
  }, 400)
}

interface Props {
  itens: MapaResultadoItem[]
  /** identifica o rascunho (etapa ou "todas" + numero da etapa) */
  chave: string
  armaDistanciaDe: (item: MapaResultadoItem) => string
  onFechar: () => void
  /** chamado quando todos foram gravados (limpa a selecao no mapa) */
  onConcluido: (qtd: number) => void
  avisar: (m: string, t: 'success' | 'error') => void
}

export function LancamentoSilhuetaLote({ itens, chave, armaDistanciaDe, onFechar, onConcluido, avisar }: Props) {
  const createResultado = useCreateResultado()
  const updateResultado = useUpdateResultado()
  const chaveDraft = `${chave}:${itens.map(i => String(i.idinscricao)).sort().join(',')}`

  const [valores, setValores] = useState<Record<string, Valores>>(() => {
    const ini: Record<string, Valores> = {}
    for (const i of itens) {
      ini[String(i.idinscricao)] = i.tem_resultado
        ? { galinha: String(i.galinha ?? 0), porco: String(i.porco ?? 0), peru: String(i.peru ?? 0), carneiro: String(i.carneiro ?? 0) }
        : { ...ZERO }
    }
    // rascunho guardado no aparelho (mesma etapa e mesmos atiradores)
    try {
      const salvo = JSON.parse(localStorage.getItem(DRAFT_KEY) || 'null')
      if (salvo && salvo.chave === chaveDraft && salvo.valores) {
        for (const k of Object.keys(ini)) if (salvo.valores[k]) ini[k] = { ...ini[k], ...salvo.valores[k] }
      }
    } catch { /* sem rascunho */ }
    return ini
  })
  const [pendentes, setPendentes] = useState<string[]>(() => itens.map(i => String(i.idinscricao)))
  const [erros, setErros] = useState<Record<string, string>>({})
  const [salvando, setSalvando] = useState(false)
  const inputsRef = useRef<Array<HTMLInputElement | null>>([])

  useEffect(() => {
    try { localStorage.setItem(DRAFT_KEY, JSON.stringify({ chave: chaveDraft, valores })) } catch { /* ignora */ }
  }, [valores, chaveDraft])

  useEffect(() => {
    document.body.style.overflow = 'hidden'
    const t = setTimeout(() => { inputsRef.current[0]?.focus(); inputsRef.current[0]?.select() }, 50)
    return () => { document.body.style.overflow = ''; clearTimeout(t) }
  }, [])

  const visiveis = itens.filter(i => pendentes.includes(String(i.idinscricao)))

  function setValor(id: string, f: Figura, v: string) {
    setValores(prev => ({ ...prev, [id]: { ...prev[id], [f]: v } }))
  }

  // Enter = proximo campo (passa para o proximo atirador no fim da linha)
  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>, idx: number) {
    if (e.key !== 'Enter') return
    e.preventDefault()
    const prox = inputsRef.current[idx + 1]
    if (prox) { prox.focus(); prox.select() }
  }

  async function salvar(imprimir: boolean) {
    if (salvando) return
    setSalvando(true)
    const okIds: string[] = []
    const novosErros: Record<string, string> = {}
    const cupons: (CupomSilhueta & { armaDistancia: string })[] = []
    for (const item of visiveis) {
      const id = String(item.idinscricao)
      const v = valores[id]
      const dados = { galinha: num(v.galinha), porco: num(v.porco), peru: num(v.peru), carneiro: num(v.carneiro) }
      try {
        if (item.tem_resultado && item.idresultado) {
          await updateResultado.mutateAsync({ id: Number(item.idresultado), data: dados })
        } else {
          await createResultado.mutateAsync({ idinscricao: Number(item.idinscricao), ...dados })
        }
        okIds.push(id)
        cupons.push({ atirador_nome: item.atirador_nome, modalidade_nome: item.modalidade_nome, valores: v, armaDistancia: armaDistanciaDe(item) })
      } catch (err: any) {
        novosErros[id] = err?.response?.data?.error ?? err?.message ?? 'Erro ao salvar'
      }
    }
    setSalvando(false)
    setErros(novosErros)

    if (imprimir && cupons.length) await imprimirCupons(cupons, avisar)

    const faltam = visiveis.map(i => String(i.idinscricao)).filter(id => !okIds.includes(id))
    if (faltam.length === 0) {
      try { localStorage.removeItem(DRAFT_KEY) } catch { /* ignora */ }
      onConcluido(okIds.length + (itens.length - visiveis.length))
      return
    }
    setPendentes(faltam)
    avisar(`${okIds.length} gravado(s); ${faltam.length} com erro — confira e tente de novo`, 'error')
  }

  let idxInput = 0

  return (
    <>
      <div className="hidden md:block fixed inset-0 z-40 bg-black/50" />
      <div
        className="fixed z-50 bg-compumaq-pretoSuave overflow-y-auto inset-0 md:inset-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-full md:max-w-4xl md:max-h-[92vh] md:rounded-xl md:shadow-xl"
        data-lote-silhueta
      >
        <div className="sticky top-0 z-10 bg-compumaq-pretoSuave border-b border-compumaq-dourado/25 flex items-center justify-between px-4 py-3 md:px-6 md:py-4">
          <div>
            <h2 className="text-base md:text-lg font-semibold text-compumaq-texto">Lancar Silhueta — {visiveis.length} atirador(es)</h2>
            <p className="text-xs text-compumaq-textoSuave">
              {new Set(itens.map(i => i.modalidade_nome)).size > 1 ? 'Varias modalidades' : itens[0]?.modalidade_nome} · Enter passa para o proximo campo
            </p>
          </div>
          <button
            type="button"
            onClick={onFechar}
            disabled={salvando}
            className="w-9 h-9 flex items-center justify-center rounded-full text-compumaq-textoSuave hover:bg-compumaq-preto text-xl touch-manipulation"
            aria-label="Fechar"
          >
            ✕
          </button>
        </div>

        <div className="p-3 md:p-6 space-y-3 pb-8">
          {visiveis.map(item => {
            const id = String(item.idinscricao)
            const v = valores[id]
            return (
              <div
                key={id}
                data-lote-linha={id}
                className={`rounded-xl border p-3 ${erros[id] ? 'border-red-400 bg-red-50/10' : 'border-compumaq-dourado/25'}`}
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="min-w-0">
                    <p className="font-bold text-compumaq-doradoClaro truncate">{item.atirador_nome}</p>
                    <p className="text-xs font-semibold text-compumaq-texto truncate" data-lote-modalidade>{item.modalidade_nome}</p>
                    <p className="text-xs text-compumaq-textoSuave truncate">
                      {item.clube_nome} · {item.categoria}
                      {item.tem_resultado && <span className="ml-1 text-amber-500">(ja lancado — sera atualizado)</span>}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-[10px] uppercase text-compumaq-textoSuave">Total</p>
                    <p className="text-2xl font-bold text-compumaq-doradoClaro leading-none" data-lote-total>{total(v)}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {FIGURAS.map(f => {
                    const meuIdx = idxInput++
                    const n = num(v[f])
                    return (
                      <div key={f} className="flex flex-col items-center rounded-lg border border-compumaq-dourado/25 p-1.5">
                        <span className="text-xs font-bold text-compumaq-textoSuave mb-1">{f.charAt(0).toUpperCase() + f.slice(1)}</span>
                        <div className="flex items-center gap-1">
                          <button type="button" tabIndex={-1} onClick={() => n > 0 && setValor(id, f, String(n - 1))}
                            className="w-8 h-8 rounded-lg bg-compumaq-preto text-compumaq-texto text-lg font-bold touch-manipulation">−</button>
                          <input
                            ref={el => { inputsRef.current[meuIdx] = el }}
                            type="number" inputMode="numeric" pattern="[0-9]*" min={0}
                            value={v[f]}
                            onFocus={e => e.target.select()}
                            onChange={e => setValor(id, f, e.target.value)}
                            onKeyDown={e => onKeyDown(e, meuIdx)}
                            data-lote-campo={`${id}:${f}`}
                            className="w-12 text-center text-lg font-bold border rounded-lg py-1 border-compumaq-dourado/25 text-compumaq-texto focus:outline-none focus:ring-2 focus:ring-compumaq-dourado"
                          />
                          <button type="button" tabIndex={-1} onClick={() => setValor(id, f, String(n + 1))}
                            className="w-8 h-8 rounded-lg bg-compumaq-dourado/15 text-compumaq-doradoClaro text-lg font-bold touch-manipulation">+</button>
                        </div>
                      </div>
                    )
                  })}
                </div>
                {erros[id] && <p className="mt-2 text-xs text-red-500" data-lote-erro>{erros[id]}</p>}
              </div>
            )
          })}

          <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-3 border-t border-compumaq-dourado/25">
            <button
              type="button"
              onClick={onFechar}
              disabled={salvando}
              className="w-full sm:w-auto px-4 py-3.5 sm:py-2.5 border border-compumaq-dourado/25 text-sm rounded-xl hover:bg-compumaq-preto font-medium touch-manipulation"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={() => salvar(true)}
              disabled={salvando}
              data-lote-salvar-imprimir
              className="w-full sm:w-auto px-4 py-3.5 sm:py-2.5 bg-compumaq-preto border border-compumaq-dourado/50 text-compumaq-texto text-base sm:text-sm rounded-xl disabled:opacity-50 font-bold touch-manipulation"
            >
              {salvando ? '...' : 'Salvar e imprimir todos'}
            </button>
            <button
              type="button"
              onClick={() => salvar(false)}
              disabled={salvando}
              data-lote-salvar
              className="w-full sm:w-auto px-6 py-3.5 sm:py-2.5 bg-compumaq-dourado text-compumaq-texto text-base sm:text-sm rounded-xl hover:bg-compumaq-doradoClaro disabled:opacity-50 font-bold touch-manipulation"
            >
              {salvando ? 'Salvando...' : `Salvar todos (${visiveis.length})`}
            </button>
          </div>
        </div>
      </div>
    </>
  )
}
